import { useEffect, useState } from 'react';

import { api } from '../api/client';
import { TEMPLATES, getTemplate } from '../templates';
import type { CatalogChart, ChartData, DataSource, SavedChart } from '../types';
import { CustomHtmlChart } from './CustomHtmlChart';

/**
 * Build a chart: pick where the data comes from, then either choose a template
 * or describe what you want and let the model write it.
 */

type Mode = 'template' | 'ai';

interface Props {
  editing?: SavedChart | null;
  onSaved: (chart: SavedChart) => void;
  onCancel: () => void;
}

export const ChartStudio = ({ editing, onSaved, onCancel }: Props) => {
  const [sources, setSources] = useState<DataSource[]>([]);
  const [source, setSource] = useState(editing?.source ?? 'appstore');
  const [catalog, setCatalog] = useState<CatalogChart[]>([]);
  const [chartId, setChartId] = useState(editing?.chartId ?? '');

  const [mode, setMode] = useState<Mode>(editing?.kind === 'custom' ? 'ai' : 'template');
  const [template, setTemplate] = useState(editing?.template ?? 'bar');
  const [html, setHtml] = useState(editing?.html ?? '');

  const [name, setName] = useState(editing?.name ?? '');
  const [prompt, setPrompt] = useState('');
  const [notes, setNotes] = useState('');

  const [data, setData] = useState<ChartData | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.sources().then(setSources).catch((err: Error) => setError(err.message));
  }, []);

  useEffect(() => {
    setCatalog([]);
    api
      .catalog(source)
      .then((charts) => {
        setCatalog(charts);
        // Keep the current selection if it still exists in the new source.
        setChartId((current) =>
          charts.some((c) => c.chartId === current) ? current : (charts[0]?.chartId ?? ''),
        );
      })
      .catch((err: Error) => setError(err.message));
  }, [source]);

  // Preview data drives both the template preview and the AI's view of the shape.
  useEffect(() => {
    if (!chartId) {
      setData(null);
      return;
    }
    let cancelled = false;
    setData(null);
    setError(null);

    api
      .query({ source, chartId })
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });

    return () => {
      cancelled = true;
    };
  }, [source, chartId]);

  const selected = catalog.find((c) => c.chartId === chartId);

  const generate = async () => {
    if (!prompt.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.generateChart({
        source,
        chartId,
        prompt,
        // Passing the current fragment turns a second prompt into an edit
        // rather than a fresh start.
        previousHtml: html || undefined,
      });
      setHtml(result.html);
      setNotes(result.notes);
      if (!name.trim()) setName(result.title);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!name.trim() || !chartId) return;
    setBusy(true);
    setError(null);
    try {
      const saved = await api.saveChart({
        id: editing?.id,
        name: name.trim(),
        source,
        chartId,
        params: editing?.params ?? {},
        kind: mode === 'ai' ? 'custom' : 'template',
        template: mode === 'template' ? template : undefined,
        html: mode === 'ai' ? html : undefined,
        description: selected?.title,
      });
      onSaved(saved);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const canSave = Boolean(name.trim() && chartId) && (mode === 'template' || Boolean(html));

  return (
    <div className="studio">
      <div className="studio__panel">
        <h2>{editing ? 'Edit chart' : 'New chart'}</h2>

        <label>
          <span>Data source</span>
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            {sources.map((s) => (
              <option key={s.id} value={s.id} disabled={!s.available}>
                {s.label}
                {s.available ? '' : ' — not connected yet'}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Chart</span>
          <select value={chartId} onChange={(e) => setChartId(e.target.value)}>
            {catalog.length === 0 && <option value="">No charts available</option>}
            {catalog.map((c) => (
              <option key={c.chartId} value={c.chartId}>
                {c.title}
              </option>
            ))}
          </select>
        </label>

        {selected && selected.requiredParams.length > 0 && (
          <p className="hint">
            Needs {selected.requiredParams.join(', ')} — the platform supplies this from the
            app filter at view time.
          </p>
        )}

        <label>
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Downloads by month" />
        </label>

        <div className="tabs" role="tablist">
          <button
            role="tab"
            aria-selected={mode === 'template'}
            className={mode === 'template' ? 'active' : ''}
            onClick={() => setMode('template')}
          >
            Template
          </button>
          <button
            role="tab"
            aria-selected={mode === 'ai'}
            className={mode === 'ai' ? 'active' : ''}
            onClick={() => setMode('ai')}
          >
            Build with AI
          </button>
        </div>

        {mode === 'template' ? (
          <div className="template-list">
            {TEMPLATES.map((t) => (
              <button
                key={t.id}
                className={`template-card ${template === t.id ? 'active' : ''}`}
                onClick={() => setTemplate(t.id)}
              >
                <strong>{t.name}</strong>
                <span>{t.description}</span>
                <em>{t.suits}</em>
              </button>
            ))}
          </div>
        ) : (
          <div className="ai-panel">
            <label>
              <span>Describe the chart</span>
              <textarea
                rows={5}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={
                  html
                    ? 'Describe a change — e.g. "colour the top three bars differently and add value labels"'
                    : 'e.g. a grouped bar chart of downloads by month, with the highest month highlighted'
                }
              />
            </label>
            <button className="primary" onClick={generate} disabled={busy || !prompt.trim() || !chartId}>
              {busy ? 'Generating…' : html ? 'Apply change' : 'Generate chart'}
            </button>
            {notes && <p className="hint">{notes}</p>}
            {html && (
              <details>
                <summary>Edit the HTML directly</summary>
                <textarea
                  className="code"
                  rows={14}
                  value={html}
                  onChange={(e) => setHtml(e.target.value)}
                  spellCheck={false}
                />
              </details>
            )}
          </div>
        )}

        {error && <p className="error">{error}</p>}

        <div className="studio__actions">
          <button onClick={onCancel}>Cancel</button>
          <button className="primary" onClick={save} disabled={!canSave || busy}>
            {editing ? 'Save changes' : 'Create chart'}
          </button>
        </div>
      </div>

      <div className="studio__preview">
        <header>Preview</header>
        <div className="studio__canvas">
          {!chartId && <div className="chart-empty">Pick a chart to preview it</div>}
          {chartId && !data && !error && <div className="chart-loading">Loading data…</div>}
          {data && mode === 'template' && getTemplate(template).render(data)}
          {data && mode === 'ai' && html && (
            <CustomHtmlChart html={html} data={data} title={name || 'Preview'} />
          )}
          {data && mode === 'ai' && !html && (
            <div className="chart-empty">Describe a chart and generate it to see a preview</div>
          )}
        </div>
        {data && (
          <footer className="studio__meta">
            {data.rows.length} rows · {data.columns.join(', ')}
          </footer>
        )}
      </div>
    </div>
  );
};
